/**
 * Voice note recorder — tiny utility hook around MediaRecorder.
 * Returns { recording, start, stop, cancel, elapsed, supported }.
 * stop() resolves with { blob, duration, mimeType }.
 *
 * iPhone/Safari note:
 * Safari is much happier with AAC/MP4 containers than WebM/Opus.
 * We therefore prefer audio/mp4 on Apple touch devices, then fall back
 * through the browser-supported list for Chrome/Android/Desktop.
 */
import { useEffect, useRef, useState } from 'react';

function isAppleTouchDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  return /iPad|iPhone|iPod/i.test(ua)
    || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function supportsMime(type) {
  if (typeof window === 'undefined') return false;
  if (typeof window.MediaRecorder?.isTypeSupported !== 'function') return false;
  try { return window.MediaRecorder.isTypeSupported(type); }
  catch { return false; }
}

export function useVoiceRecorder() {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(null);
  const tickRef = useRef(null);
  const streamRef = useRef(null);
  const stopResolveRef = useRef(null);
  const cancelledRef = useRef(false);

  const supported = typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof window.MediaRecorder !== 'undefined';

  const cleanup = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    mediaRef.current = null;
    startedAtRef.current = null;
  };

  const getBestMimeType = () => {
    if (!supported) return '';

    const applePreferred = [
      'audio/mp4',
      'audio/aac',
      'audio/webm;codecs=opus',
      'audio/webm',
    ];

    const defaultPreferred = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/aac',
    ];

    const candidates = isAppleTouchDevice() ? applePreferred : defaultPreferred;
    return candidates.find(supportsMime) || '';
  };

  const start = async () => {
    if (!supported) throw new Error('Recording not supported in this browser.');
    if (recording || mediaRef.current?.state === 'recording') return;

    cancelledRef.current = false;

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    chunksRef.current = [];

    const mimeType = getBestMimeType();
    let recorder;

    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch (err) {
      // Some Safari/WebKit builds report a type as supported but still
      // reject it at construction. Fall back to the browser default.
      recorder = new MediaRecorder(stream);
    }

    mediaRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data?.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      const duration = startedAtRef.current
        ? (Date.now() - startedAtRef.current) / 1000
        : 0;

      const resolvedMimeType = recorder.mimeType || mimeType || chunksRef.current[0]?.type || 'audio/webm';
      const blob = cancelledRef.current
        ? null
        : new Blob(chunksRef.current, { type: resolvedMimeType });

      cleanup();
      setRecording(false);
      setElapsed(0);

      if (stopResolveRef.current) {
        stopResolveRef.current(blob ? { blob, duration, mimeType: resolvedMimeType } : null);
        stopResolveRef.current = null;
      }

      chunksRef.current = [];
      cancelledRef.current = false;
    };

    // Timeslice helps mobile Safari flush audio chunks more reliably.
    recorder.start(1000);
    startedAtRef.current = Date.now();
    setRecording(true);
    setElapsed(0);

    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
  };

  const stop = () => {
    return new Promise((resolve) => {
      if (!mediaRef.current || mediaRef.current.state !== 'recording') {
        resolve(null);
        return;
      }

      cancelledRef.current = false;
      stopResolveRef.current = resolve;

      try { mediaRef.current.requestData?.(); }
      catch { /* non-fatal */ }

      mediaRef.current.stop();
    });
  };

  const cancel = () => {
    cancelledRef.current = true;

    if (mediaRef.current && mediaRef.current.state === 'recording') {
      stopResolveRef.current = () => {};
      try { mediaRef.current.stop(); }
      catch { cleanup(); }
    } else {
      cleanup();
      chunksRef.current = [];
      cancelledRef.current = false;
    }

    setRecording(false);
    setElapsed(0);
  };

  useEffect(() => () => cleanup(), []);

  return { recording, elapsed, start, stop, cancel, supported };
}
